import React, { useEffect, useState } from 'react';
import { Loader2, Trash2, MailCheck, Inbox } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const ContactManager = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/contact');
      setItems(data);
    } catch (err) {
      toast({
        title: 'Gagal memuat pesan',
        description: err?.response?.data?.detail || 'Terjadi kesalahan',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await api.patch(`/admin/contact/${id}/read`);
      await load();
    } catch (err) {
      toast({ title: 'Gagal memperbarui status', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus pesan ini?')) return;
    try {
      await api.delete(`/admin/contact/${id}`);
      toast({ title: 'Pesan dihapus' });
      await load();
    } catch (err) {
      toast({ title: 'Gagal menghapus', variant: 'destructive' });
    }
  };

  const unreadCount = items.filter((m) => !m.read).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-white font-bold text-2xl tracking-tight">Pesan Kontak</h2>
          <p className="text-neutral-500 text-sm mt-1">
            Pesan dari form kontak di website ({unreadCount} baru belum dibaca)
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-[#E9C46A] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-[#0f0f0f] border border-white/[0.06] rounded-2xl py-14 flex flex-col items-center gap-3">
          <Inbox className="w-8 h-8 text-neutral-600" />
          <p className="text-neutral-400 font-medium">Belum ada pesan kontak.</p>
          <p className="text-neutral-600 text-sm">Pesan dari form kontak akan tampil di sini.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((m) => (
            <div
              key={m.id}
              className={`bg-[#0f0f0f] border rounded-2xl p-5 ${
                m.read ? 'border-white/[0.06]' : 'border-[#D4A94F]/40 bg-[#D4A94F]/5'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-semibold truncate">{m.name}</h3>
                    {!m.read && (
                      <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#D4A94F]/20 text-[#E9C46A] border border-[#D4A94F]/40">
                        Baru
                      </span>
                    )}
                  </div>
                  <p className="text-neutral-400 text-sm truncate">{m.email}</p>
                </div>
                <div className="flex items-center gap-1">
                  {!m.read && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 px-2 text-neutral-400 hover:text-[#E9C46A]"
                      onClick={() => handleMarkRead(m.id)}
                      title="Marka sebagai dibaca"
                    >
                      <MailCheck className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 px-2 text-neutral-400 hover:text-red-400"
                    onClick={() => handleDelete(m.id)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <p className="text-neutral-300 mt-3 whitespace-pre-line leading-relaxed">{m.message}</p>
              <p className="text-[11px] text-neutral-600 mt-3">{formatDate(m.created_at)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactManager;